import React, { useMemo } from 'react';
import { Edit3, Eye, Trash2 } from 'lucide-react';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { CardList } from '@/components/ui/CardList';
import { formatBillionCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';
import type { LinkedService } from '../types/categoriesDetail.types';

interface CategoryServiceTableBlockProps {
  items: LinkedService[];
  onView: (item: LinkedService) => void;
  onEdit: (item: LinkedService) => void;
  onDelete: (item: LinkedService) => void;
}

export function CategoryServiceTableBlock({ items, onView, onEdit, onDelete }: CategoryServiceTableBlockProps) {
  const sortedItems = useMemo(() => [...items].sort((a, b) => a.name.localeCompare(b.name, 'vi')), [items]);

  if (items.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-200 p-8 text-center">
        <p className="text-sm text-slate-500">Không có dịch vụ liên kết.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-slate-900 px-1">Dịch vụ liên kết ({items.length})</h3>
      
      {/* Mobile View */}
      <div className="lg:hidden">
        <CardList className="grid-cols-1 md:grid-cols-2">
          {sortedItems.map((item) => (
            <div key={item.id} className="rounded-md border border-slate-100 bg-white p-3 shadow-sm space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{item.name}</p>
                  <p className="text-xs text-slate-500">{item.code}</p>
                </div>
                <span className={cn(
                  'rounded-md px-2 py-0.5 text-[10px] font-medium uppercase',
                  item.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                )}>
                  {item.status === 'active' ? 'Mở' : 'Tắt'}
                </span>
              </div>
              
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-slate-500">Đơn giá:</span>
                <span className="text-sm font-medium text-emerald-700">{formatBillionCurrency(item.price)}</span>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <ActionMenu
                  items={[
                    { label: 'Xem', icon: Eye, onClick: () => onView(item) },
                    { label: 'Sửa', icon: Edit3, onClick: () => onEdit(item) },
                    { label: 'Xóa', icon: Trash2, destructive: true, onClick: () => onDelete(item) },
                  ]}
                />
              </div>
            </div>
          ))}
        </CardList>
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block rounded-md border border-slate-100 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-500 border-b border-slate-100 font-normal">
            <tr>
              <th className="p-3 font-normal">Tên dịch vụ</th>
              <th className="p-3 font-normal">Mã</th>
              <th className="p-3 font-normal text-right">Đơn giá</th>
              <th className="p-3 font-normal text-center">Trạng thái</th>
              <th className="p-3 w-10 font-normal" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedItems.map((item) => (
              <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                <td className="p-3 font-medium text-slate-900">{item.name}</td>
                <td className="p-3 text-slate-500">{item.code}</td>
                <td className="p-3 text-right font-medium text-emerald-700">{formatBillionCurrency(item.price)}</td>
                <td className="p-3 text-center">
                  <span className={cn(
                    'inline-flex rounded-md px-2 py-0.5 text-xs font-semibold',
                    item.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                  )}>
                    {item.status === 'active' ? 'Hoạt động' : 'Tạm dừng'}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <ActionMenu
                    items={[
                      { label: 'Xem chi tiết', icon: Eye, onClick: () => onView(item) },
                      { label: 'Chỉnh sửa', icon: Edit3, onClick: () => onEdit(item) },
                      { label: 'Xóa dịch vụ', icon: Trash2, destructive: true, onClick: () => onDelete(item) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
